// FILE: src/components/LearnerDashboard.js
//
// PM Pixel learner dashboard — mission-control redesign (2026-06).
//
// Two-column layout on desktop (primary content + sticky coaching rail),
// stacked on mobile. Sourced from the static preview at
// docs/previews/dashboard-redesign.html.
//
// Data orchestration only — every visual chunk lives in its own component:
//
//   PRIMARY COLUMN
//     ReadinessHero        — big ring + status + trend + Next Best Action + 6 sub-bars
//     JourneyRoadmap       — 7-node visual progression (Baseline → Certified)
//     NextUpCard / ThisWeekCard / RecentSessionsCard  — 3-col mini grid
//     MilestonesAtAGlance  — Foundation / Mastery / Completion buckets + Progress ring
//
//   COACH RAIL (sticky on desktop)
//     CoachInsightCard / CoachSaysCard / FocusAreasCard
//     NextSessionCard      — persistent Start Now CTA
//     StreakCard / QuickLinksCard
//
// Existing services + modals (StudyPlanEditor, RecordOutcomeModal,
// LearnerDashboardEmpty, CertTrackSwitcher, CoachLayout) are reused as-is.

import React, { useEffect, useMemo, useState } from 'react';
import styles from './LearnerDashboard.module.css';

import LearnerDashboardEmpty from './LearnerDashboardEmpty';
import CertTrackSwitcher from '../certification/CertTrackSwitcher';
import ScreenHeader from '../shared/ScreenHeader';
import FoundingTesterBadge from '../founding-testers/FoundingTesterBadge';
import RecordOutcomeModal from '../certification/RecordOutcomeModal';
import StudyPlanEditor from '../studyplan/StudyPlanEditor';

import DashboardGrid from './DashboardGrid';
import DiscordPromo from '../shared/DiscordPromo';
import ReadinessHero from '../readiness/ReadinessHero';
import JourneyRoadmap from '../journey/JourneyRoadmap';
import NextUpCard from './NextUpCard';
import ThisWeekCard from './ThisWeekCard';
import RecentSessionsCard from './RecentSessionsCard';
import MilestonesAtAGlance from '../achievements/MilestonesAtAGlance';
import CoachRail from '../coach/CoachRail';

import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { getExamConfig, getActiveCertIds, getBaselineColumn } from '../../config/exams';
import {
  computeReadiness,
  computeConfidenceForecast,
  loadSessionsForReadiness,
  loadReadinessSnapshots,
  setLearnerBaseline,
} from '../../services/readinessService';
import { loadActiveStudyPlan, pickTodaysActivity } from '../../services/studyPlanService';
import { loadEarnedAchievements } from '../../services/achievementService';
import { deriveStreak } from '../../services/streakService';
import { computeWeeklyTrend } from '../../services/weeklyTrendService';
import {
  loadSecondaryPerformance,
  buildSecondaryGuidance,
} from '../../services/secondaryDimensionService';
import { isFeatureEnabled } from '../../config/featureFlags';
import { hasLocalIntent } from '../../services/journeyService';

const ACTIVE_CERTS = getActiveCertIds();

export default function LearnerDashboard({ onNavigate, onStartSession, onStartCert }) {
  const { profile, refresh } = useLearnerAuth();

  const [activeCert, setActiveCert] = useState(null);
  const [readiness, setReadiness]   = useState(null);
  const [sessions, setSessions]     = useState([]);
  const [snapshots, setSnapshots]   = useState([]);
  const [forecast, setForecast]     = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [planActivities, setPlanActivities] = useState([]);
  const [earnedByKey, setEarnedByKey] = useState({});
  const [secondaryPerf, setSecondaryPerf] = useState(null);
  const [loading, setLoading]       = useState(true);

  const [editorOpen, setEditorOpen]   = useState(false);
  const [outcomeModalOpen, setOutcomeModalOpen] = useState(false);
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);

  // Baselines per cert — unchanged from prior implementation.
  const baselines = useMemo(() => {
    const out = {};
    for (const cert of ACTIVE_CERTS) {
      const col = getBaselineColumn(cert);
      out[cert] = profile?.[col] || null;
    }
    return out;
  }, [profile]);

  const certsWithBaseline = ACTIVE_CERTS.filter((c) => !!baselines[c]);
  const hasAnyBaseline    = certsWithBaseline.length > 0;

  // Load dashboard data on mount + when cert/baseline changes.
  useEffect(() => {
    if (!hasAnyBaseline) return undefined;
    let cancelled = false;
    setLoading(true);

    (async () => {
      const perCert = {};
      for (const cert of certsWithBaseline) {
        const list = await loadSessionsForReadiness(cert, baselines[cert]);
        perCert[cert] = list;
      }
      if (cancelled) return;

      const pickedCert = activeCert && perCert[activeCert]
        ? activeCert
        : pickMostRecentCert(perCert);

      const list = perCert[pickedCert] || [];
      const r = computeReadiness({
        examId: pickedCert, sessions: list, baselineAt: baselines[pickedCert],
      });
      setSessions(list);
      setReadiness(r);
      setActiveCert(pickedCert);

      const certConfig = getExamConfig(pickedCert);
      const userTargetPct = profile?.target_score_pct;
      const targetForForecast = typeof userTargetPct === 'number'
        ? userTargetPct
        : (certConfig?.pass_target_pct || 75);

      const [snaps, planResult, earnedRows, secPerf] = await Promise.all([
        loadReadinessSnapshots(pickedCert, 20),
        loadActiveStudyPlan(pickedCert),
        loadEarnedAchievements({ limit: 100 }),
        // Self-gates on getSecondaryDimension(pickedCert): resolves to an empty
        // result (no Supabase round trip) for certs with no secondary dimension.
        loadSecondaryPerformance(pickedCert),
      ]);
      if (cancelled) return;

      setSecondaryPerf(secPerf);

      setSnapshots(snaps);
      setForecast(computeConfidenceForecast(snaps, targetForForecast));
      setActivePlan(planResult.plan || null);
      setPlanActivities(planResult.activities || []);

      const byKey = {};
      for (const row of (earnedRows || [])) {
        byKey[row.achievement_key] = row;
      }
      setEarnedByKey(byKey);

      setLoading(false);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyBaseline, profile?.baseline_at_pmp, profile?.baseline_at_cpmai, activeCert]);

  // ── Derived state ────────────────────────────────────────────────────────
  const today = new Date();
  const targetDate = profile?.target_exam_id === activeCert ? (profile?.target_exam_date || null) : null;

  const todaysActivity = useMemo(
    () => pickTodaysActivity(planActivities, new Date().toISOString().slice(0, 10)),
    [planActivities],
  );

  const daysUntil = useMemo(() => {
    if (!targetDate) return null;
    const t = new Date(targetDate);
    if (!Number.isFinite(t.getTime())) return null;
    return Math.ceil((t.getTime() - today.getTime()) / 86400000);
  }, [targetDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const weeklyTrend = useMemo(() => computeWeeklyTrend(snapshots), [snapshots]);
  const streak = useMemo(() => deriveStreak(sessions), [sessions]);
  // Config-driven secondary-dimension coaching guidance; null for certs with
  // no secondary dimension or no qualifying data (callers keep domain guidance).
  const secondaryGuidance = useMemo(
    () => buildSecondaryGuidance(activeCert, secondaryPerf),
    [activeCert, secondaryPerf],
  );

  // Empty state delegates to LearnerDashboardEmpty (unchanged path).
  // One primary onboarding intervention at a time (Journey Activation rule):
  // when the journey surface is enabled and this learner has shown no
  // practice intent on this device (no mode selection ever recorded), the
  // journey panel on the home screen owns the moment — the dashboard hands
  // off instead of stacking the legacy baseline modal on top. Learners who
  // HAVE shown intent (picked a mode before) keep the baseline modal: for
  // them it's a next step, not an onboarding pitch. Learners with sessions
  // on another device fall through to home, where the journey surface
  // detects their sessions and stays hidden — a plain home screen, never
  // two interventions.
  const journeyOwnsOnboarding =
    !hasAnyBaseline
    && isFeatureEnabled('journeyOnboardingEnabled')
    && !hasLocalIntent();

  useEffect(() => {
    if (journeyOwnsOnboarding) onNavigate?.('home');
  }, [journeyOwnsOnboarding, onNavigate]);

  if (journeyOwnsOnboarding) {
    return <div className={styles.page} data-testid="learner-dashboard-page" />;
  }

  if (!hasAnyBaseline) {
    return (
      <div className={styles.page} data-testid="learner-dashboard-page">
        <LearnerDashboardEmpty
          isOpen={true}
          onClose={() => onNavigate?.('home')}
          onStartCert={onStartCert}
          onStartFirstSession={onStartSession}
          baselines={baselines}
          displayName={profile?.display_name || null}
        />
      </div>
    );
  }

  async function handleRecalibrate() {
    const newDate = await setLearnerBaseline(activeCert);
    if (newDate) await refresh?.();
  }

  const examLabel = !activeCert
    ? 'Your'
    : (getExamConfig(activeCert)?.displayName || activeCert.toUpperCase());

  const targetDateLabel = targetDate
    ? new Date(targetDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  // Header chip status — "On track" if forecast clears the target, else "—".
  const onTrack = forecast?.sufficient && forecast?.projectedDate && targetDate
    ? new Date(forecast.projectedDate) <= new Date(targetDate)
    : null;

  return (
    <div className={styles.page} data-testid="learner-dashboard-page">

      {/* ── HEADER: standard topmast (ScreenHeader banner) ── */}
      <ScreenHeader
        variant="banner"
        coachPose="idle"
        eyebrow="Your Certification Journey"
        title={<>{(examLabel || '').split(/([®™])/).map((p, i) => /[®™]/.test(p) ? <span key={i} className={styles.regMark}>{p}</span> : p)}<br />Dashboard</>}
        subtitle={<FoundingTesterBadge auto size={32} />}
        promo={<DiscordPromo />}
        actions={(
        <div className={styles.headerStats}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Days until exam</span>
            <span className={styles.statVal}>
              {daysUntil != null ? Math.max(daysUntil, 0) : '—'}
            </span>
            {!targetDateLabel && (
              <button type="button" className={styles.statLink}
                onClick={() => setEditorOpen(true)}
                data-testid="dashboard-set-target-date"
              >Set a target date</button>
            )}
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Target date</span>
            <span className={styles.statVal}>{targetDateLabel || '—'}</span>
            {onTrack === true && <span className={styles.statOk}>On track</span>}
            {onTrack === false && <span className={styles.statWarn}>Behind pace</span>}
          </div>
        </div>
        )}
      />

      <CertTrackSwitcher
        activeCert={activeCert}
        baselines={baselines}
        onSelect={setActiveCert}
        onStartCert={() => setBaselineDialogOpen(true)}
        testIdPrefix="dashboard"
      />

      {loading ? (
        <p className={styles.loading}>Loading your progress…</p>
      ) : (
        <DashboardGrid
          primary={
            <>
              <ReadinessHero
                examLabel={examLabel}
                readiness={readiness}
                trend={weeklyTrend}
                todaysActivity={todaysActivity}
                onSeeBreakdown={() => onNavigate?.('readiness')}
                onStartSession={onStartSession}
              />

              <JourneyRoadmap
                readiness={readiness}
                sessions={sessions}
                baselineAt={baselines[activeCert]}
                earnedByKey={earnedByKey}
                onViewMilestones={() => onNavigate?.('milestones')}
              />

              <div className={styles.miniGrid}>
                <NextUpCard
                  activity={todaysActivity}
                  onStartSession={onStartSession}
                  onViewPlan={() => onNavigate?.('plan')}
                />
                <ThisWeekCard
                  activities={planActivities}
                  onViewPlan={() => activePlan ? onNavigate?.('plan') : setEditorOpen(true)}
                />
                <RecentSessionsCard
                  sessions={sessions}
                  onViewHistory={() => onNavigate?.('history')}
                />
              </div>

              <MilestonesAtAGlance
                activeCert={activeCert}
                onViewAll={() => onNavigate?.('milestones')}
              />

              <div className={styles.footerActions}>
                <button type="button" className={styles.primaryAction}
                  onClick={onStartSession}
                  data-testid="dashboard-continue-practice"
                >Continue practicing {examLabel} →</button>
                <button type="button" className={styles.secondaryAction}
                  onClick={() => setOutcomeModalOpen(true)}
                  data-testid="dashboard-record-outcome"
                >Record exam outcome</button>
              </div>
            </>
          }
          rail={
            <CoachRail
              examLabel={examLabel}
              examId={activeCert}
              readiness={readiness}
              trend={weeklyTrend}
              weakPhases={readiness?.weakPhases || []}
              secondaryPerformance={secondaryPerf}
              secondaryGuidance={secondaryGuidance}
              activity={todaysActivity}
              streak={streak}
              onStartSession={onStartSession}
              onNavigate={onNavigate}
              onRecalibrate={handleRecalibrate}
            />
          }
        />
      )}

      {/* ── Modals (unchanged) ── */}
      <StudyPlanEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          await refresh?.();
          if (activeCert) {
            const r = await loadActiveStudyPlan(activeCert);
            setActivePlan(r.plan || null);
            setPlanActivities(r.activities || []);
          }
        }}
        activeCert={activeCert}
        readiness={readiness}
        initialTargetDate={profile?.target_exam_id === activeCert ? profile?.target_exam_date : null}
        initialPersonalTarget={typeof profile?.target_score_pct === 'number' ? profile.target_score_pct : null}
      />

      <RecordOutcomeModal
        isOpen={outcomeModalOpen}
        onClose={() => setOutcomeModalOpen(false)}
        activeCert={activeCert}
      />

      <LearnerDashboardEmpty
        isOpen={baselineDialogOpen}
        onClose={() => setBaselineDialogOpen(false)}
        onStartCert={onStartCert}
        baselines={baselines}
        displayName={profile?.display_name || null}
      />
    </div>
  );
}

// Helper: when no active cert is set yet, pick the one with most recent activity.
function pickMostRecentCert(perCert) {
  let best = null;
  let bestT = -Infinity;
  for (const cert of Object.keys(perCert)) {
    const list = perCert[cert] || [];
    const t = list[0] ? new Date(list[0].startedAt).getTime() : 0;
    if (Number.isFinite(t) && t > bestT) { bestT = t; best = cert; }
  }
  return best || Object.keys(perCert)[0] || null;
}
